import { HubItemKind } from "../hubItemCategories";
import { environmentHttpClient } from "client/Api/httpClient";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import {
	HubItemLayoutComposerViewModel,
	HubItemLayoutSaveModel,
	HubItemMediaComposerViewModel,
	HubItemMediaSaveModel
} from "../common.composer";
import { HubItemImageRestrictionsModel } from "../common";
import { DocumentLocationViewModel } from "../hubSettings";

export type CreateHubTemplateOptionsModel = {
	basedOnAnnouncementId?: number;
	basedOnKnowledgeItemId?: number;
	basedOnEventId?: number;
	basedOnTemplateId?: number;
};

export type CreateHubTemplateComposerViewModel = {
	title: string | null;
	layout: HubItemLayoutComposerViewModel;
	media: HubItemMediaComposerViewModel;
	imageRestrictions: HubItemImageRestrictionsModel;
	defaultImageLocation: DocumentLocationViewModel | null;
	isPixabayImageGalleryAvailable: boolean;
};

export type EditHubTemplateComposerViewModel = {
	id: number;
	kind: HubItemKind;
	name: string;
	title: string | null;
	layout: HubItemLayoutComposerViewModel | null;
	media: HubItemMediaComposerViewModel;
	imageRestrictions: HubItemImageRestrictionsModel;
	defaultImageLocation: DocumentLocationViewModel | null;
	isPixabayImageGalleryAvailable: boolean;
};

export type HubTemplateSaveModel = {
	title: string;
	layout: HubItemLayoutSaveModel;
	media: HubItemMediaSaveModel;
};

export type CreateHubTemplateRequestModel = {
	model: HubTemplateSaveModel;
	kind: HubItemKind;
	name: string;
};

export enum CreateHubTemplateResultKind {
	Success = 1,
	MaxNumberOfTemplatesReached = 2
}

export type SuccessfulCreateHubTemplateResultModel = {
	kind: CreateHubTemplateResultKind.Success;
	id: number;
};

export type MaxTemplatesReachedCreateHubTemplateResultModel = {
	kind: CreateHubTemplateResultKind.MaxNumberOfTemplatesReached;
};

export type CreateHubTemplateResultModel =
	| SuccessfulCreateHubTemplateResultModel
	| MaxTemplatesReachedCreateHubTemplateResultModel;

export type HubTemplateManageListItemModel = {
	id: number;
	name: string;
	createdOn: Date | string;
	modifiedOn: Date | string;
	createdBy: UserIdentityModel | null;
	modifiedBy: UserIdentityModel | null;
};

export type HubTemplateRenameModel = {
	id: number;
	newName: string;
};

export function list(kind: HubItemKind, signal: AbortSignal): Promise<readonly HubTemplateManageListItemModel[]> {
	return environmentHttpClient.get<readonly HubTemplateManageListItemModel[]>(
		"UiApi/Hub/Templates/Management",
		{ kind },
		{ signal }
	);
}

export function create(request: CreateHubTemplateRequestModel, signal: AbortSignal): Promise<CreateHubTemplateResultModel> {
	return environmentHttpClient.post<CreateHubTemplateResultModel>(
		"UiApi/Hub/Templates/Management",
		request,
		{ signal }
	);
}

export function getCreateViewModel(
	options: CreateHubTemplateOptionsModel,
	signal: AbortSignal
): Promise<CreateHubTemplateComposerViewModel> {
	return environmentHttpClient.get<CreateHubTemplateComposerViewModel>(
		"UiApi/Hub/Templates/Management/Create",
		options,
		{ signal }
	);
}

export function getEditViewModel(
	id: number,
	signal: AbortSignal
): Promise<EditHubTemplateComposerViewModel> {
	return environmentHttpClient.get<EditHubTemplateComposerViewModel>(
		`UiApi/Hub/Templates/Management/Update/${id}`,
		undefined,
		{ signal }
	);
}

export function update(
	id: number,
	model: HubTemplateSaveModel,
	signal: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		`UiApi/Hub/Templates/Management/${id}`,
		model,
		{ signal }
	);
}

export function rename(model: HubTemplateRenameModel, signal: AbortSignal): Promise<void> {
	return environmentHttpClient.put<void>(
		"UiApi/Hub/Templates/Management/Rename",
		model,
		{ signal }
	);
}

export function deleteTemplates(ids: readonly number[], signal: AbortSignal): Promise<void> {
	return environmentHttpClient.delete<void>(
		"UiApi/Hub/Templates/Management",
		ids,
		{ signal }
	);
}